/**
 * R-28 — Surfaces blocked/rolled-back SED rebuilds with diff summary + override.
 */
import React, { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import type { SiteEntity } from '../../types/site';
import type { SedDiff, SedSnapshot } from '../../types/sedRebuild';
import { SedRebuildBlockedError } from '../../types/sedRebuild';
import { SED_CONFIG } from '../../config/sedConfig';

export interface SedRebuildBlockedState {
  rebuildId: string;
  siteId: string;
  diff: SedDiff;
  before: SedSnapshot;
  after: SedSnapshot;
}

interface Props {
  site: SiteEntity;
  /** In-memory blocked state from a just-failed rebuild attempt. */
  blockedState?: SedRebuildBlockedState | null;
  onClearBlockedState?: () => void;
}

export const SedRebuildBlockedBanner: React.FC<Props> = ({
  site,
  blockedState,
  onClearBlockedState,
}) => {
  const [rationale, setRationale] = useState('');
  const [overriding, setOverriding] = useState(false);

  const rebuildId = blockedState?.rebuildId ?? site.sedRebuildBlockedRebuildId;
  const diff = blockedState?.diff;
  const show =
    blockedState != null
    || (site.sedRebuildBlocked === true && rebuildId != null);

  if (!show || !rebuildId) return null;

  const completenessDelta = diff?.completenessDelta;
  const lost = diff?.verifiedSectionsLost ?? [];

  const handleOverride = async () => {
    const trimmed = rationale.trim();
    if (trimmed.length < SED_CONFIG.OVERRIDE_RATIONALE_MIN_CHARS) {
      toast.error(
        `Override rationale must be at least ${SED_CONFIG.OVERRIDE_RATIONALE_MIN_CHARS} characters`,
      );
      return;
    }
    setOverriding(true);
    try {
      const { rebuildSedWithGuard } = await import('../../lib/sed/sedRebuildOrchestrator');
      const { useStore } = await import('../../store');
      const siteNow = useStore.getState().siteRag.sites.find(s => s.id === site.id);
      const { projectDocsForSed } = await import('../../services/sedBootstrap');
      const { docsEligibleForEvidence } = await import('../../lib/evidenceDocEligibility');
      const includeDocIds = siteNow
        ? docsEligibleForEvidence(siteNow).map(d => d.id)
        : [];
      await rebuildSedWithGuard(
        site.id,
        { includeDocIds },
        {
          allowOverride: true,
          overrideRationale: trimmed,
          trigger: 'manual-rebuild',
        },
      );
      toast.success('SED rebuild committed with override');
      onClearBlockedState?.();
    } catch (e) {
      if (e instanceof SedRebuildBlockedError) {
        toast.error('Override rebuild still blocked — check thresholds or rationale');
      } else {
        toast.error(`Override failed: ${(e as Error)?.message ?? 'unknown'}`);
      }
    } finally {
      setOverriding(false);
    }
  };

  return (
    <div
      role="alert"
      className="flex flex-col gap-3 px-3 py-3 rounded-lg border border-danger/50 bg-danger/5"
      data-testid="sed-rebuild-blocked-banner"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 text-danger shrink-0 mt-0.5" />
        <div className="flex-1 text-[11px] text-text-primary space-y-1">
          <strong className="uppercase tracking-widest text-danger">
            SED rebuild rolled back
          </strong>
          <p>
            The latest rebuild would have reduced evidence quality. Your prior SED was restored automatically.
          </p>
          {site.sedRebuildBlockedReason ? (
            <p className="text-text-secondary">{site.sedRebuildBlockedReason}</p>
          ) : null}
          {typeof completenessDelta === 'number' ? (
            <p>
              Completeness change: <strong>{completenessDelta > 0 ? '+' : ''}{completenessDelta}pp</strong>
              {' · '}
              Verified sections lost: <strong>{lost.length}</strong>
              {lost.length > 0 ? ` (${lost.join(', ')})` : ''}
            </p>
          ) : null}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <span className="text-[10px] text-text-muted uppercase tracking-widest">
          Rollback complete
        </span>
        <a
          href={`/admin/sed-rebuild-log?siteId=${encodeURIComponent(site.id)}&rebuildId=${encodeURIComponent(rebuildId)}`}
          className="inline-flex h-7 items-center px-2 border border-border text-[10px] uppercase tracking-widest hover:bg-muted/30"
          data-testid="sed-rebuild-blocked-investigate"
        >
          Investigate
        </a>
      </div>

      <div className="space-y-2 border-t border-border/60 pt-2">
        <label className="text-[10px] uppercase tracking-widest text-text-secondary">
          Override with rationale ({SED_CONFIG.OVERRIDE_RATIONALE_MIN_CHARS}+ chars)
        </label>
        <textarea
          className="w-full min-h-[4rem] text-xs border border-border bg-background px-2 py-1.5"
          value={rationale}
          onChange={e => setRationale(e.target.value)}
          data-testid="sed-rebuild-override-rationale"
        />
        <Button
          variant="destructive"
          size="sm"
          className="h-7 text-[10px] uppercase tracking-widest"
          disabled={overriding}
          onClick={() => void handleOverride()}
          data-testid="sed-rebuild-override-confirm"
        >
          {overriding ? 'Rebuilding…' : 'Override and rebuild'}
        </Button>
      </div>
    </div>
  );
};
